import { useState, useEffect } from "react";
import { useParams} from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  QrCode,
  Download,
  FileText,
  CreditCard,
  Copy,
  Check,
  Clock,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  X,
  FileSpreadsheet,
} from "lucide-react";
import logoRoxo from "../../assets/logo-roxo.png";
import { generateWeeklyReportPDF } from "../../libs/empresa-relatorio-pdf";

import axios from 'axios'

type InvoiceStatus = "PAGO" | "ATIVA" | "Inadimplente";

const statusConfig = {
  ATIVA: {
    label: "Aguardando Pagamento",
    icon: Clock,
    bgClass: "bg-accent/20",
    textClass: "text-accent-foreground",
    borderClass: "border-accent",
    dotClass: "bg-secondary",
  },
  PAGO: {
    label: "Pago",
    icon: CheckCircle2,
    bgClass: "bg-[hsl(152,60%,42%)]/10",
    textClass: "text-[hsl(152,60%,42%)]",
    borderClass: "border-[hsl(152,60%,42%)]",
    dotClass: "bg-[hsl(152,60%,42%)]",
  },
  Inadimplente: {
    label: "Inadimplente",
    icon: AlertTriangle,
    bgClass: "bg-destructive/10",
    textClass: "text-destructive",
    borderClass: "border-destructive",
    dotClass: "bg-destructive",
  },
};

type WeeklyReportPdfPayload = {
  companyName: string;
  companyId: string;
  cnpj: string;
  email: string;
  category: string;
  period: {
    start: string;
    end: string;
  };
  emissionDate: string;
  deliveries: {
    os_number: string;
    date: string;
    driver: string;
    operator: string;
    destinations: string[];
    value: number;
  }[];
  totalStops: number;
  avgPerRequest: number;
  avgValuePerStop: number;

  ridesTotal: number;
  additionalValues: number;
  adminFee: number;
  reportTotal: number;

  dailyVolumes: {
    day: string;
    label: string;
    value: number;
    count: number;
  }[];
  fileName?: string;
};

const InvoicePage = () => {
  const baseURL = '/cobranca'
  const cobrancaApi = axios.create({
    baseURL: baseURL,
  })
  const {id} = useParams();

  const [status, setStatus] = useState<InvoiceStatus>("ATIVA");
  const [showBoleto, setShowBoleto] = useState(false);
  const [loadingBoleto, setLoadingBoleto] = useState(false);
  const [copiedPix, setCopiedPix] = useState(false);
  const [copiedBoleto, setCopiedBoleto] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [loadingPlanilha, setLoadingPlanilha] = useState(false);
  const [loadingNoTaFiscal, setLoadingNoTaFiscal] = useState(false);
  const [showBoletoConfirm, setShowBoletoConfirm] = useState(false);
  const [loadingDownloadBoleto, setLoadingDownloadBoleto] = useState(false);

  const [data, setData] = useState<any>(null);
  const [codicoBoleto, setCodicoBoleto] = useState("");
  const [qrRegenerated, setQrRegenerated] = useState(false);
  const [loadingQrRegen, setLoadingQrRegen] = useState(false);
  const [qrLoading, setQrLoading] = useState(true);


  const invoice = {
    cliente: data?.["Nome do Estabelecimento"] || "",
    cnpj: data?.CNPJ || "",
    validade: data?.["Vencimento da Cobranca"],
    valor: data?.["Valor da Cobranca"] || 0,
    descricao: data?.["Descricao da Cobranca"] || "",
    informacoes: "Após a data de vencimento será cobrado um juros fixo de 3% e outro de 0,33% por dia de atraso.",
    pixCode: data?.image_base64 || "",
    boletoCode: codicoBoleto || "",
    pixCopy: data?.emv_payload || "",
    nfUrl: data?.nfe || false
  };
  const cfg = statusConfig[status];


  const buscarDados = async () => {
    let resposta;

    try {
      resposta = await cobrancaApi.get("/procurarPorId", { params: { value: id } });
    } catch (err) {
      console.error(err);
      return;
    }

    const dados = resposta.data;

    setStatus(dados.status as InvoiceStatus);
    setQrRegenerated(dados.txid === "" ? false : true);
    setData(dados);

    const boleto = dados?.boleto;

    const codigoBoleto =
      boleto && typeof boleto === "object"
        ? boleto.barcode || boleto.identificationField || ""
        : "";

    setCodicoBoleto(codigoBoleto);
    setShowBoleto(!!codigoBoleto);
  };
  
  const formatCurrency = (value: string | number) => {
    if (!value) return "R$ 0,00";

    const numberValue = typeof value === "string"
      ? Number(value.replace(",", "."))
      : value;

    return numberValue.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };


  const formatDateNoTimezone = (dateStr: string) => {
    if (!dateStr) return "";

    const [year, month, day] = dateStr.split("T")[0].split("-");

    return `${day}/${month}/${year}`;
  };

  const lidarComprovante = () => {
    if (!data?.comprovante) return;

    window.open(data.comprovante, "_blank");
  };

  const handleCopy = async (text: string, type: "pix" | "boleto") => {
    await navigator.clipboard.writeText(text);
    if (type === "pix") {
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2500);
    } else {
      setCopiedBoleto(true);
      setTimeout(() => setCopiedBoleto(false), 2500);
    }
  };

  const lidarGerarQrCode = async(event) =>{
    //Chama end-point para gerar qr code

  try {
      setLoadingQrRegen(true);

      const response = await cobrancaApi.get('/criarQrCodeAsaas', {params: {value:{id: id}}})

      const novoQr = response.data.data;

      setData((prev: any) => ({
        ...prev,
        image_base64: novoQr.image_base64,
        emv_payload: novoQr.emv_payload,
      }));

      setQrRegenerated(true);
    } catch (err) {
      console.error("Erro ao regenerar QR Code:", err);
    } finally {
      setLoadingQrRegen(false);
    }
  }


  const gerarBoleto = async () => {
    try {
      setLoadingBoleto(true);

      const response = await cobrancaApi.post('/gerarBoletos', { cobranca: data });

      const codigo = response.data.identificationField

      setCodicoBoleto(codigo);
      setShowBoleto(true);
      

    } catch (err) {
      console.error("Erro ao gerar boleto:", err);
    } finally {
      setLoadingBoleto(false);
    }
  };

  const baixarNotaFiscal = async () => {
    setLoadingNoTaFiscal(true)
    try {
      const response = await axios.get(
        `https://portal-financeiro-505113.rj.r.appspot.com/api/public/nf-pdf?id=${id}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = url;
      link.download = `nota-fiscal-${id}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar nota fiscal:', error);
    } finally {
    setLoadingNoTaFiscal(false);
    }
  };

  const lidarBaixarBoleto = () => { 
    //Baixar boleto

    var codigo = ('charge' in data.boleto) === true ? data.boleto.charge : data.boleto.bankSlipUrl

    var link = document.createElement('a');
    link.href = codigo;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    setLoadingDownloadBoleto(false);
  } 

const baixarPlanilha = async () => {
  try {
    setLoadingPlanilha(true)

    const response = await axios.get(
      "https://portal-financeiro-505113.rj.r.appspot.com/api/public/fatura-planilha",
      {
        params: {
        cnpj: data["CNPJ"],
        valor: Number(data["Valor da Cobranca"]),
         vencimento: data["Vencimento da Cobranca"],
        },
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(response.data);

    const link = document.createElement("a");
    link.href = url;
    link.download = `fatura-${ data["CNPJ"]}.xlsx`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error("Erro ao baixar planilha:", err);
  } finally {
     setLoadingPlanilha(false)
  }
}

const baixarRelatorio = async () => {
  try {
    setLoadingRelatorio(true);

    const response = await axios.post(
      "https://portal-financeiro-505113.rj.r.appspot.com/api/public/fatura-pdf",
      {
        cnpj: data["CNPJ"],
        valor: Number(data["Valor da Cobranca"]),
        vencimento: data["Vencimento da Cobranca"],
      },
      {
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" })
    );

    const link = document.createElement("a");
    link.href = url;
    link.download = `fatura-${data["ID da Empresa"]}.pdf`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Erro ao baixar relatório:", err);
  } finally {
    setLoadingRelatorio(false);
  }
};
  const simulatePayment = () => {
      setShowPaymentSuccess(true);
      setTimeout(() => {
        setStatus("paid");
        setTimeout(() => setShowPaymentSuccess(false), 3000);
      }, 1500);
  }


  useEffect(() => {
    if (!id || status === "PAGO") return;

    const interval = setInterval(() => {
      buscarDados();
    }, 10000);

    return () => clearInterval(interval);
  }, [id, status]);

  useEffect(() => {
    if (id) {
      buscarDados();
    }
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => setQrLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Payment Success Overlay */}
      <AnimatePresence>
        {showPaymentSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-card rounded-2xl p-10 shadow-2xl text-center max-w-sm mx-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 10 }}
                className="w-20 h-20 rounded-full bg-[hsl(152,60%,42%)]/10 flex items-center justify-center mx-auto mb-5"
              >
                <CheckCircle2 className="w-10 h-10 text-[hsl(152,60%,42%)]" />
              </motion.div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                Pagamento Confirmado!
              </h3>
              <p className="text-muted-foreground text-sm">
                Seu pagamento foi processado com sucesso.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Bar */}
      <div className="bg-[#6c447f] py-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/80"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <p className="text-white/90 text-xs font-medium tracking-wide">Taon Pay — Sistema Seguro de Pagamentos</p>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <img src={logoRoxo} alt="Logo" className="h-10 w-auto" />
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg.bgClass} ${cfg.textClass} ${cfg.borderClass}`}
          >
            <span className={`w-2 h-2 rounded-full ${cfg.dotClass} animate-pulse`} />
            {cfg.label}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div className="flex flex-col lg:flex-row gap-6">
        {/* Invoice Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden lg:flex-1"
        >
          <div className="bg-[#6c447f] px-5 sm:px-6 py-4">
            <h1 className="text-primary-foreground font-bold text-lg">
              Detalhes da Cobrança
            </h1>
          </div>
          <div className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailItem label="Cliente" value={invoice.cliente} />
              <DetailItem label="CNPJ" value={invoice.cnpj} />
              <DetailItem label="Validade" value={formatDateNoTimezone(invoice.validade)} />
              <DetailItem label="Valor" value={formatCurrency(invoice.valor)} highlight />
            </div>
            <div className="border-t border-border pt-4">
              <DetailItem label="Descrição" value={invoice.descricao} />
            </div>
            <div className="border-t border-border pt-4">
              <DetailItem label="Informações" value={invoice.informacoes} />
            </div>
          </div>
        </motion.div>


        {/* Payment Options */}
        {status !== "PAGO" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-4 lg:flex-1"
          >
            {/* PIX - Primary */}
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <QrCode className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-foreground">Pagar com Pix</h2>
                    <p className="text-xs text-muted-foreground">Pagamento instantâneo</p>
                  </div>
                </div>
                 <div className="flex flex-col items-center gap-4">
                  {status === "Inadimplente" && !qrRegenerated ? (
                    <div className="w-48 h-48 sm:w-56 sm:h-56 flex items-center justify-center">
                      {loadingQrRegen ? (
                        <Loader2 className="w-10 h-10 text-primary animate-spin" />
                      ) : (
                        <button
                          onClick={lidarGerarQrCode}
                          className="flex flex-col items-center gap-3 px-6 py-4 rounded-xl bg-[hsl(0,55%,50%)] text-white font-medium text-sm hover:opacity-90 transition-opacity active:scale-[0.97] animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]"
                        >
                          <QrCode className="w-8 h-8" />
                          Gerar novo QR Code
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      className="w-48 h-48 sm:w-56 sm:h-56 bg-white rounded-xl flex items-center justify-center border border-border cursor-pointer hover:border-primary transition-colors p-3"
                      onClick={simulatePayment}
                      title="Clique para simular pagamento"
                    >
                      {qrLoading ? (
                        <Loader2 className="w-12 h-12 text-primary animate-spin" />
                      ) : (
                      <QRCodeSVG
                        value={invoice.pixCopy}
                        size={200}
                        level="M"
                        includeMargin={false}
                        fgColor="#4a2d5e"
                      />
                       )}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground text-center max-w-xs">
                    {status === "Inadimplente" && !qrRegenerated
                      ? "O QR Code expirou. Gere um novo com os valores atualizados."
                      : "Escaneie o QR Code acima com o aplicativo do seu banco ou copie o código abaixo"}
                  </p>

                  {(status !== "Inadimplente" || qrRegenerated) && (
                    <button
                      onClick={() => handleCopy(invoice.pixCopy, "pix")}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity"
                    >
                      {copiedPix ? (
                        <>
                          <Check className="w-4 h-4" />
                          Código copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copiar código Pix
                        </>
                      )}
                    </button>
                  )}
                </div>


              </div>
            </div>

            {/* Secondary Actions */}
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              <div
                className={`grid grid-cols-1 lg:divide-y-0 lg:divide-x divide-y divide-border ${
                  invoice.nfUrl ? "lg:grid-cols-4" : "lg:grid-cols-3"
                }`}
              >                
              <button
                 onClick={baixarRelatorio}
                  className="flex flex-col items-center justify-center gap-2 py-6 px-3 hover:bg-muted/40 transition-colors text-center group cursor-pointer"                >
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {loadingRelatorio ? (
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                    <span className="block text-sm font-semibold text-foreground">
                      {loadingRelatorio ? "Preparando..." : "Relatório"}
                    </span>
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">PDF</span>
                  </div>
              </button>

              <button
                  onClick={baixarPlanilha}
                  className="flex flex-col items-center justify-center gap-2 py-6 px-3 hover:bg-muted/40 transition-colors text-center group cursor-pointer"
                >
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {loadingPlanilha ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-foreground">
                      {loadingPlanilha ? "Preparando..." : "Planilha"}
                    </span>
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Excel</span>
                  </div>
                </button>

              <button
                onClick={() => {
                  if (showBoleto) {
                      if (loadingDownloadBoleto) return;
                      setLoadingDownloadBoleto(true);
                      lidarBaixarBoleto()
                  }else{
                    setShowBoletoConfirm(true);

                  }
                }}
                  className="flex flex-col items-center justify-center gap-2 py-6 px-3 hover:bg-muted/40 transition-colors text-center group cursor-pointer"
              >
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {loadingBoleto || loadingDownloadBoleto ? (
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  ) : showBoleto ? (
                    <Download className="w-5 h-5 text-primary" />
                  ) : (
                    <FileText className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                    <span className="block text-sm font-semibold text-foreground">
                      {loadingBoleto ? "Gerando Boleto..." : loadingDownloadBoleto ? "Preparando..." : showBoleto ? "Baixar Boleto" : "Gerar Boleto"}
                    </span>
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Bancário</span>
                </div>
              </button>
              {invoice.nfUrl && (

                <button
                onClick={baixarNotaFiscal}
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center gap-2 py-6 px-3 hover:bg-muted/40 transition-colors text-center group cursor-pointer"
                >
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                      <span className="block text-sm font-semibold text-foreground">Nota Fiscal</span>
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">NF-e</span>
                </div>
              </button>
              )}
              </div>
            </div>

            {/* Boleto Expanded */}
            <AnimatePresence>
              {showBoleto && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="bg-card rounded-xl border border-border shadow-sm p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm text-foreground">Código do Boleto</h3>
                    </div>
                    <div className="bg-muted rounded-lg p-3 mb-3">
                      <p className="text-xs sm:text-sm font-mono text-foreground break-all leading-relaxed">
                        {invoice.boletoCode}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopy(invoice.boletoCode, "boleto")}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-primary text-primary font-medium text-sm hover:bg-primary/5 transition-colors"
                    >
                      {copiedBoleto ? (
                        <>
                          <Check className="w-4 h-4" />
                          Código copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copiar código do boleto
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Paid State - inside flex row */}
        {status === "PAGO" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-card rounded-2xl border border-[hsl(152,60%,42%)]/20 shadow-sm p-8 text-center lg:flex-1"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 12, delay: 0.2 }}
              className="w-16 h-16 rounded-full bg-[hsl(152,60%,42%)]/10 flex items-center justify-center mx-auto mb-4"
            >
              <CheckCircle2 className="w-8 h-8 text-[hsl(152,60%,42%)]" />
            </motion.div>
            <h2 className="text-lg font-bold text-foreground mb-1">Pagamento Confirmado</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Obrigado! Seu pagamento foi recebido com sucesso.
            </p>

            <div className="flex flex-col items-center gap-3">
                <button 
                  onClick={() => lidarComprovante()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary/10 text-primary font-medium text-sm hover:bg-primary/15 transition-colors">
                  <Download className="w-4 h-4" />
                  Baixar Comprovante
                </button>
                <button 
                  onClick={baixarRelatorio}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary/10 text-primary font-medium text-sm hover:bg-primary/15 transition-colors">
                  <Download className="w-4 h-4" />
                  Baixar Relatório
                </button>
            </div>
          </motion.div>
        )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-8 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground text-center">
            Em caso de dúvidas, entre em contato com nosso suporte Financeiro
          </p>
          <a
            href="https://wa.me/15558374215"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white font-medium text-sm hover:bg-[#1ebe5a] transition-colors shadow-md"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Falar com Financeiro
          </a>
        </div>
      </footer>
            <AnimatePresence>
        {showBoletoConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm"
            onClick={() => setShowBoletoConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-card rounded-2xl p-6 shadow-2xl max-w-sm mx-4 w-full border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-secondary" />
                </div>
                <h3 className="text-base font-bold text-foreground">Confirmar geração do boleto</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Ao gerar o boleto, será acrescentada uma <span className="font-semibold text-foreground">taxa de R$ 2,50</span> referente a custas bancárias para emissão. Deseja continuar?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBoletoConfirm(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowBoletoConfirm(false);
                    setLoadingBoleto(true);
                    gerarBoleto();
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Confirmar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DetailItem = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <div>
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
      {label}
    </span>
    <p
      className={`mt-0.5 text-sm ${
        highlight ? "text-primary font-bold text-lg" : "text-foreground font-medium"
      }`}
    >
      {value}
    </p>
  </div>
);

export default InvoicePage;